import { toggleMainMessageFold, toggleSubsFold } from './legacyBridge/legacyActions'
import { normalizeSubsSection } from './contracts/subsNavigation'
import type { SubsDisclosureAction } from './contracts/subsDisclosure'
import { getMsgDeskRuntime } from './runtime/MsgDeskRuntime'
import { treeStore } from './state'
import { dispatchDeskNavigation } from './deskNavigation'

export * from './contracts/subsDisclosure'

type RootSection = 0 | 1

const rootContract = (section: RootSection): 'subs.answers.rootList' | 'subs.submsges.rootList' => (
  section === 0 ? 'subs.answers.rootList' : 'subs.submsges.rootList'
)

const pagingContract = (section: RootSection): 'subs.paging.answers' | 'subs.paging.submsges' => (
  section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
)

function rootIsVisible(section: RootSection): boolean {
  return treeStore.getState().chromeByContract[rootContract(section)]?.visible !== false
}

async function reconcileRootFold(section: RootSection, folded: boolean): Promise<void> {
  const runtime = getMsgDeskRuntime()
  await new Promise<void>((resolve) => runtime.setTimeout(resolve, 100))
  treeStore.mutate.setContractVisible(rootContract(section), !folded)
  treeStore.mutate.setContractVisible(pagingContract(section), !folded)
  treeStore.mutate.setContractCss(pagingContract(section), 'display', folded ? 'none' : 'flex')
}

function preserveStandaloneSectionMode(mode: string, target: unknown): void {
  if (mode !== 'answers' && mode !== 'submsges') return

  void getMsgDeskRuntime().facade.appMsgObj?.legacy?.whenIdle?.(`subsDisclosure:${mode}`).then(() => {
    if (treeStore.getState().renderProjection.mode === 'subs') {
      dispatchDeskNavigation({ mode, target })
    }
  })
}

async function toggleAllRootSections(target: unknown): Promise<void> {
  const shouldFold = rootIsVisible(0) || rootIsVisible(1)
  toggleSubsFold(target, 0, -1)

  // Legacy's full-fold branch hides its panel wrappers. React owns those
  // wrappers, so reconcile the equivalent state on the registered root lists.
  await Promise.all([
    reconcileRootFold(0, shouldFold),
    reconcileRootFold(1, shouldFold),
  ])
}

// NESTED-FOLD reconciliation: legacy _toggle_subs flips the tree's showed
// counter and then fadeToggles the nested ul — but that ul is React-owned
// (SubFeed), so the DOM op cannot fold it. Mirror the authoritative model
// state (loaded > 0 && showed <= 0 -> hidden) into the store after the
// legacy command, same pattern as reconcileRootFold. During a first-click
// load (nothing loaded yet) the flag stays visible; the inserted children
// arrive with showed > 0.
function reconcileChildrenFold(parentElId: number, section: RootSection): void {
  try {
    const tree = getMsgDeskRuntime().facade.msgData(parentElId)?.['subs_tree']
    if (!tree) return
    const isSubsMode = treeStore.getState().renderProjection.mode !== 'msg'
    const counters = tree.counters?.(section === 1)
    const loaded = Array.isArray(counters) ? Number(counters[1]) || 0 : 0
    const showed = Number(tree.showed?.(isSubsMode, section === 1)) || 0
    treeStore.mutate.setSubChildrenHidden(parentElId, section, loaded > 0 && showed <= 0)
  } catch {
    // pre-boot / missing tree: nothing to reconcile
  }
}

export function dispatchSubsDisclosure(request: SubsDisclosureAction): unknown {
  if (request.action === 'toggle-main') return toggleMainMessageFold()
  if (request.action === 'toggle-all') return toggleAllRootSections(request.target)

  const section = normalizeSubsSection(request.section)
  const parentElId = request.action === 'toggle-children' ? request.parentElId : 0
  const mode = treeStore.getState().renderProjection.mode
  const shouldFold = request.action === 'toggle-section' && rootIsVisible(section)
  const result = toggleSubsFold(request.target, parentElId, section)
  if (request.action === 'toggle-section') {
    void reconcileRootFold(section, shouldFold)
    preserveStandaloneSectionMode(mode, request.target)
  }
  if (request.action === 'toggle-children' && parentElId > 0) {
    reconcileChildrenFold(parentElId, section)
  }
  return result
}
